// گزارشِ صفحه‌ی تستِ دستی برای سربرگِ «تستِ دستی» در ادمین (مایگریشن‌های 0161–0166).
//
// SQL فقط داده‌ی خام می‌دهد: کاتالوگ، تیکت‌ها و پلِ تیکت↔سناریو، اساین‌ها،
// شیءِ وضعیتِ هر تستر همان‌طور که صفحه‌ی qa/ ذخیره‌اش کرده، و رویدادهای لاگ.
// اینکه «انجام شده» یعنی چه، آخرین نتیجه کدام است، فیلترِ و/یا چطور ارزیابی
// می‌شود و ساعت‌ها چطور جمع می‌خورند این‌جا تصمیم گرفته می‌شود تا با
// `npm test` آزموده شود — همان تقسیمِ کاری usersReport و healthReport.
//
// از 0166 هر اساین مالِ یک «تیکت» است — یا تیکتِ Jira یا تیکتِ نام‌دار (Q-1…)
// — و یک سناریو می‌تواند در چند تیکت باشد. نتیجه‌ی هر اجرا مالِ (تیکت، سناریو)
// است، با کلیدِ «KEY/ID» در qa_state.

import { discardKey, discardedRuns } from './qaClock.ts';

/** 'review' (0186): the run's only bugs are the tester's own view — waiting for the owner, neither pass nor fail. */
export type QaStatus = 'new' | 'progress' | 'pass' | 'fail' | 'blocked' | 'done' | 'review';
export const FINISHED: QaStatus[] = ['pass', 'fail', 'blocked', 'done', 'review'];

/**
 * سکوهای سناریو با «و» و «یا» (WHOSUP-332؛ «یا» از 2026-10-22) — همان قاعده‌ی `qa-ticket.mjs`.
 * ستونِ platform: «,» = و، «|» = یا، و «و» قوی‌تر است («web,ios»، «web|phone»).
 * «phone» یعنی گوشی، فرقی ندارد اندروید یا آیفون؛ «both» ِ قدیمی = «web,android».
 */
export const QA_PLATFORMS = ['web', 'android', 'ios', 'phone'] as const;
const KNOWN = QA_PLATFORMS as readonly string[];

const canonAtoms = (a: string[]): string[] => {
  const uniq = [...new Set(a.flatMap((x) => (x === 'both' ? ['web', 'android'] : [x.trim()])).filter(Boolean))];
  return [...QA_PLATFORMS.filter((x) => uniq.includes(x)), ...uniq.filter((x) => !KNOWN.includes(x))];
};

/** گزینه‌های «یا»، هر کدام سکوهای «و»: «web|phone» → [['web'], ['phone']]. */
export function qaPlatformAlts(p: string | null | undefined): string[][] {
  return String(p ?? '').split('|').map((a) => canonAtoms(a.split(','))).filter((a) => a.length);
}

/** همه‌ی سکوهایی که سناریو نام می‌برد، در هر گزینه‌ای (برای برچسب و فیلتر). */
export function qaPlatforms(p: string | null | undefined): string[] {
  return canonAtoms(qaPlatformAlts(p).flat());
}

/**
 * کوچک‌ترین دسته‌های دستگاه (وب/اندروید/آیفون) که همه‌ی این سکوها را راضی می‌کنند —
 * یک سناریو یا همه‌ی سناریوهای یک دور. همان platformNeed ِ `qa-ticket.mjs`، platNeed ِ
 * qa/index.html و باتِ تستر؛ با هم عوض شوند.
 */
export const QA_DEVICES = ['web', 'android', 'ios'] as const;
export function qaPlatformNeed(platforms: (string | null | undefined)[]): string[][] {
  const fs = platforms.map((p) => qaPlatformAlts(p).map((a) => a.filter((x) => KNOWN.includes(x))).filter((a) => a.length))
    .filter((alts) => alts.length);
  if (!fs.length) return [];
  const has = (have: string[], x: string) => (x === 'phone' ? have.includes('android') || have.includes('ios') : have.includes(x));
  const ok = [1, 2, 3, 4, 5, 6, 7].map((m) => QA_DEVICES.filter((_, i) => m & (1 << i)) as string[])
    .filter((have) => fs.every((alts) => alts.some((a) => a.every((x) => has(have, x)))));
  return ok.filter((s) => !ok.some((o) => o.length < s.length && o.every((x) => s.includes(x))));
}

/** «وب و (اندروید یا آیفون)»، «وب یا اندروید یا آیفون»، «(وب و اندروید) یا آیفون». */
export function qaNeedText(sets: string[][], label: Record<string, string>, and = ' و ', or = ' یا '): string {
  if (!sets.length) return '';
  const name = (s: string[]) => s.map((x) => label[x] ?? x).join(and);
  if (sets.length === 1) return name(sets[0]);
  const common = QA_DEVICES.filter((x) => sets.every((s) => s.includes(x))) as string[];
  const rest = sets.map((s) => s.filter((x) => !common.includes(x)));
  if (rest.every((r) => r.length === 1)) {
    const either = rest.map((r) => label[r[0]] ?? r[0]).join(or);
    return common.length ? `${name(common)}${and}(${either})` : either;
  }
  return sets.map((s) => (s.length > 1 ? `(${name(s)})` : name(s))).join(or);
}

export interface QaScenario {
  id: string;
  v: number;
  cat: string;
  platform: string;
  priority: string | null;
  smoke: boolean;
  /** منسوخ از 0174 — همیشه null؛ محیط از دور می‌آید (roundEnv). */
  env?: string | null;
  /** null = سناریوی کتابخانه؛ 'free'/'other' = ویژه. */
  kind: string | null;
  /** 'file' = qa/scenarios.mjs؛ 'autopilot' = `npm run qa -- put-ticket`. */
  source?: string;
  title: { en: string; fa: string };
  sort: number;
  /** متنِ کاملِ سناریو، همان شکلِ qa/scenarios.mjs. */
  body?: QaScenarioBody | null;
}
type QaText = { en: string; fa: string };
export interface QaScenarioBody {
  accounts?: string | null;
  pre?: QaText | null;
  steps?: (QaText & { check?: QaText | null })[] | null;
  expected?: QaText | null;
}

export type QaTicketKind = 'jira' | 'named';
export interface QaTicket {
  key: string;
  kind: QaTicketKind;
  title: string | null;
  title_en?: string | null;
  source: string;
  date?: string;
  created_at?: string;
  /** دورِ نام‌داری که مالِ یک تیکتِ Jira است (مایگریشن 0171): نتیجه‌اش به همان تیکت می‌رسد. */
  jira_key?: string | null;
  /** دورِ آزاد (مایگریشن 0183): بی اساین، همه‌ی تسترها می‌بینند و اولین کسی که برش دارد صاحبش می‌شود. */
  open?: boolean;
  /**
   * دورِ دوم به بعد (مایگریشن 0185): مرحله‌ای (QaLink.stage) که کامل قبول نشد دور را همان‌جا رد کرد —
   * سناریوهای مرحله‌های بعد «اجرا نشد» هستند و دور تمام‌شده است.
   */
  stopped_at?: string | null;
  stop_stage?: number | null;
}

/**
 * محیطِ یک دور: همیشه develop ('staging') — هر دور و تستِ آزاد (مالک 2026-10-27، مایگریشن 0190؛
 * تا آن روز دورِ عمومی روی پروداکشن بود، 0174). سناریو محیط ندارد. همان قاعده‌ی roundEnv در
 * supabase/functions/_shared/qa-ticket.mjs، qa/index.html و public.qa_prepare.
 */
export const roundEnv = (_t?: Pick<QaTicket, 'kind' | 'jira_key'> | null): 'staging' => 'staging';

export type QaRole = 'sanity' | 'acceptance' | 'regression' | 'assigned';
export const ROLES: QaRole[] = ['sanity', 'acceptance', 'regression', 'assigned'];
/**
 * `stage` (مایگریشن 0185) فقط در دورِ دوم به بعدِ یک تیکت: ۱ = آخرین بار رد شد، ۲ = دست‌کم یک بار رد شد،
 * ۳ = بقیه؛ وقتی دور برداشته/اساین شد از تاریخچه‌ی همان تیکت حساب شد. null = بی مرحله (مثلِ قبل).
 */
export interface QaLink { ticket_key: string; scenario_id: string; role: QaRole; sort: number; stage?: number | null }
export const STAGE_LABEL: Record<number, string> = { 1: 'آخرین بار رد شده بود', 2: 'دست‌کم یک بار رد شده بود', 3: 'بقیه' };

export interface QaTester {
  user_id: string;
  email: string;
  name: string | null;
  active: boolean;
  is_default?: boolean;
  /** شناسه‌های دستگاهِ تستر (0167) — برای پیدا کردنِ لاگِ اپ/سایتش از روی تیکتِ باگ. */
  device_ids?: string[];
  added_at: string;
}

/**
 * `user_id` یا شناسه‌ی حسابِ تستر است، یا برای دعوت‌شده‌ای که هنوز عضو نشده
 * `invite:<email>` (0163) — تا جدول‌ها و دکمه‌ی اساین هر دو را یک‌جور ببینند.
 */
export interface QaAssignment { ticket_key: string; user_id: string; assigned_at?: string }
export const INVITE_PREFIX = 'invite:';
export const inviteKey = (email: string) => `${INVITE_PREFIX}${email}`;
export const inviteEmail = (key: string) => (key.startsWith(INVITE_PREFIX) ? key.slice(INVITE_PREFIX.length) : null);

export interface QaRun { status: QaStatus; attempt: number; page: number }
export interface QaNote {
  id: string; sid: string; k: string | null; a: number; p: number; t: number;
  text: string; bug: boolean; sev: string | null; photos: string[]; deleted?: boolean;
  /** «Only against the scenario, or your own view too?» (0186): 'no' | 'yes' | 'unsure'; an admin's own note 'admin'. */
  interp?: string;
}
export interface QaHour { id: string; date: string; min: number; cat: string; desc: string; deleted?: boolean }
export interface QaStateDoc {
  tester?: string;
  runs?: Record<string, QaRun>;
  notes?: QaNote[];
  hours?: QaHour[];
}
export interface QaStateRow { user_id: string; state: QaStateDoc; updated_at: string }
/**
 * رویدادهای لاگ که این‌جا لازم‌اند: start/resume/pause برای زمان‌سنجی، و
 * finish برای «چند بار تست شده و آخرین نتیجه». بقیه (beat، page…) خوانده نمی‌شوند.
 */
export interface QaEvent {
  user_id: string;
  n: number;
  e: { type: string; t: number; sid?: string | null; k?: string | null; a?: number | null; d?: { stoppedAt?: number; status?: QaStatus } };
}

/** نتیجه‌ی یک سناریو برای یک تستر. کلیدِ اجرای داخلِ تیکت «KEY/ID» است، همان runKeyِ صفحه. */
export const runKey = (ticket: string | null, id: string) => (ticket ? `${ticket}/${id}` : id);

/** «WHOSUP-321 — عنوان» یا فقط کلید. */
export const ticketLabel = (t: Pick<QaTicket, 'key' | 'title'>) => (t.title ? `${t.key} — ${t.title}` : t.key);

/** کلیدی که ادمین برای تیکتِ Jira تایپ می‌کند؛ «whosup 321» و «321» هم قبول. */
export function normalizeJiraKey(raw: string): string | null {
  const m = raw.trim().toUpperCase().match(/^(?:WHOSUP[-\s]?)?(\d+)$/);
  return m ? `WHOSUP-${m[1]}` : null;
}

/** زمانِ واقعیِ صرف‌شده روی سناریوها، از روی ساعتی که صفحه روشن و خاموش می‌کند. */
export function clockMinutes(events: QaEvent['e'][]): number {
  // A run the tester set aside counts as never started (discardedRuns).
  const gone = discardedRuns(events);
  let running: number | null = null;
  let skip = false;
  let ms = 0;
  for (const e of [...events].sort((a, b) => a.t - b.t)) {
    if ((e.type === 'start' || e.type === 'resume') && running == null) { running = e.t; skip = gone.has(discardKey(e.k, e.sid, e.a)); }
    else if (e.type === 'pause' && running != null) {
      const end = e.d?.stoppedAt ?? e.t;
      if (end > running && !skip) ms += end - running;
      running = null;
    }
  }
  return Math.round(ms / 60000);
}

const lastOf = (xs: QaEvent[]) => xs.reduce<QaEvent | null>((a, x) => (!a || x.e.t > a.e.t ? x : a), null);
const isFinish = (x: QaEvent) => x.e.type === 'finish' && !!x.e.sid;
/**
 * «اصلاح» on the QA page (owner, 2026-10-28) records a changed result as a second 'finish' of the same
 * attempt; one attempt is still one time tested, and its latest finish is its result.
 */
export function finishesPerAttempt(events: QaEvent[]): QaEvent[] {
  const last = new Map<string, QaEvent>();
  for (const x of events.filter(isFinish)) {
    const key = [x.user_id, x.e.sid, x.e.k ?? '', x.e.a ?? 1].join('|');
    const had = last.get(key);
    if (!had || x.e.t >= had.e.t) last.set(key, x);
  }
  return [...last.values()];
}

/* ===================================================================== جدولِ سناریوها */

export interface ScenarioTableRow {
  scenario: QaScenario;
  /** تیکت‌هایی که این سناریو در آن‌هاست (از پل). */
  tickets: QaTicket[];
  /** هر کس که به یکی از آن تیکت‌ها اساین شده. */
  assignees: string[];
  /** چند بار تا آخر رفته — در هر تیکتی، از هر تستری. */
  timesTested: number;
  lastAt: number | null;
  lastStatus: QaStatus | null;
  lastBy: string | null;
  lastTicket: string | null;
}

/** یک ردیف برای هر سناریوی کتابخانه — همان چیزی که می‌شود در یک تیکت گذاشت. */
export function scenarioTable(
  catalog: QaScenario[], tickets: QaTicket[], links: QaLink[], assignments: QaAssignment[], events: QaEvent[],
): ScenarioTableRow[] {
  const finishes = finishesPerAttempt(events);
  const byKey = new Map(tickets.map((t) => [t.key, t]));
  return catalog
    .filter((sc) => sc.kind == null)
    .sort((a, b) => a.sort - b.sort)
    .map((scenario) => {
      const keys = links.filter((l) => l.scenario_id === scenario.id).map((l) => l.ticket_key);
      const last = lastOf(finishes.filter((x) => x.e.sid === scenario.id));
      return {
        scenario,
        tickets: keys.map((k) => byKey.get(k)).filter((t): t is QaTicket => !!t),
        assignees: [...new Set(assignments.filter((a) => keys.includes(a.ticket_key)).map((a) => a.user_id))],
        timesTested: finishes.filter((x) => x.e.sid === scenario.id).length,
        lastAt: last?.e.t ?? null,
        lastStatus: last?.e.d?.status ?? null,
        lastBy: last?.user_id ?? null,
        lastTicket: last?.e.k ?? null,
      };
    });
}

/* ===================================================================== جدولِ تیکت‌ها */

/** یک بارِ تمام‌شده‌ی یک سناریو در یک تیکت. */
export interface QaAttempt { n: number | null; status: QaStatus; by: string; at: number }

export interface TicketTableRow {
  ticket: QaTicket;
  /** سناریوهای پل‌شده، به ترتیبِ مرحله (اگر دارد)، بعد نقش و بعد ترتیبِ پل — همان ترتیبِ صفحه‌ی تستر. */
  scenarios: {
    scenario: QaScenario; role: QaRole; stage: number | null; status: QaStatus | null; by: string | null;
    /** هر باری که تا آخر رفت، به ترتیبِ زمان — «بار»ِ هر تستر شماره‌ی خودش را دارد. */
    attempts: QaAttempt[];
    /** دور پیش از رسیدن به مرحله‌ی این سناریو متوقف شد (0185) و این سناریو در آن اجرا نشد. */
    skipped: boolean;
    /** مسدودی که مدیر به تیکتِ اصلاح منتقل یا بی‌اثر کرد (0192) — از حکم بیرون است. */
    out: boolean;
  }[];
  assignees: string[];
  /** چند سناریو داخلِ همین تیکت قبول یا رد شده — «مسدود» نتیجه نیست (منتظرِ اجرای دوباره). */
  decided: number;
  /**
   * دورِ تمام‌شده — از فهرستِ دورهای فعال بیرون می‌رود: هر سناریو قبول یا رد شده، یا همه اجرا شده‌اند و
   * «رد»ی هست (مسدودِ کنارش حکم را عوض نمی‌کند)، یا در یک مرحله متوقف و رد شد.
   */
  finished: boolean;
  /** مرحله‌ای که دور در آن متوقف و رد شد (0185)، یا null. */
  stoppedAt: number | null;
  pass: number;
  fail: number;
  blocked: number;
  review: number;
}

/**
 * تصمیمِ مدیر روی یک «پرونده‌ی بررسی» (WHOSUP-382، 0192)، فقط آن‌چه حکم را عوض می‌کند: 'round'/'back' درخواستی
 * است که دور را «رد» می‌کند، 'fix'/'void' سناریوی مسدودِ همان اجرا را از حکم بیرون می‌برد، و 'scenario' (اصلاحِ سناریو،
 * 0195) همان اجرا را — وقتی نسخه‌ی تازه آمد (scen_state 'done') — «بی نتیجه» می‌کند: دوباره برای انجام. از 0197
 * «نتیجه‌ی سناریو» (result) ِ مدیر جای نتیجه‌ی تستر می‌نشیند: pass / fail همان، redo «بی نتیجه» — اگر سناریو اصلاح یا
 * ادیت می‌شود، از وقتی نسخه‌ی تازه آمد. همان ticketOutcome در supabase/functions/_shared/qa-ticket.mjs و
 * ticketProgress در qa/index.html — با هم عوض شوند.
 */
export interface QaTriageEffect {
  user_id: string; sid: string; k: string | null; a: number; work: string; scen_state?: string | null;
  result?: 'pass' | 'fail' | 'redo' | null; decided_at?: string | null;
}
const OUT_WORK = ['fix', 'void'];
const SCEN_WORK = ['scenario', 'edit'];
const REQUEST_WORK = ['round', 'back'];

export function ticketTable(
  tickets: QaTicket[], links: QaLink[], catalog: QaScenario[], assignments: QaAssignment[], events: QaEvent[],
  triage: QaTriageEffect[] = [],
): TicketTableRow[] {
  const finishes = finishesPerAttempt(events);
  const byId = new Map(catalog.map((s) => [s.id, s]));
  return tickets.map((ticket) => {
    const mineLinks = links.filter((l) => l.ticket_key === ticket.key);
    const staged = mineLinks.some((l) => l.stage != null);
    // A link added after the stages were set (by hand, in the admin) belongs with «the rest».
    const stageOf = (l: QaLink) => (staged ? l.stage ?? 3 : null);
    const stoppedAt = ticket.stopped_at ? ticket.stop_stage ?? null : null;
    const scenarios = mineLinks
      .sort((a, b) => (stageOf(a) ?? 0) - (stageOf(b) ?? 0) || ROLES.indexOf(a.role) - ROLES.indexOf(b.role) || a.sort - b.sort)
      .map((l) => ({ l, s: byId.get(l.scenario_id) }))
      .filter((x): x is { l: QaLink; s: QaScenario } => !!x.s)
      .map(({ l, s }) => {
        const mine = finishes.filter((x) => x.e.sid === s.id && x.e.k === ticket.key
          && ['pass', 'fail', 'blocked', 'review'].includes(x.e.d?.status ?? ''));
        const last = lastOf(mine);
        const attempts = mine.sort((a, b) => a.e.t - b.e.t)
          .map((x) => ({ n: x.e.a ?? null, status: x.e.d!.status!, by: x.user_id, at: x.e.t }));
        const stage = stageOf(l);
        const ofLast = (c: QaTriageEffect) => !!last && c.user_id === last.user_id && c.sid === s.id
          && (c.k ?? null) === (last.e.k ?? null) && (c.a ?? 1) === (last.e.a ?? 1);
        const out = triage.some((c) => OUT_WORK.includes(c.work) && ofLast(c));
        // «The scenario result» (0197): the owner's latest one on that run.
        const res = triage.filter((c) => c.result && ofLast(c))
          .reduce<QaTriageEffect | null>((m, c) => (!m || String(c.decided_at ?? '') > String(m.decided_at ?? '') ? c : m), null);
        const redo = res
          ? res.result === 'redo' && (!SCEN_WORK.includes(res.work) || res.scen_state === 'done')
          : triage.some((c) => c.work === 'scenario' && c.scen_state === 'done' && ofLast(c));
        const status = redo ? null : res && res.result !== 'redo' ? res.result! : last?.e.d?.status ?? null;
        return { scenario: s, role: l.role, stage, status, by: last?.user_id ?? null, attempts,
          skipped: stoppedAt != null && (stage ?? 3) > stoppedAt && !last, out };
      });
    // A blocked one the owner moved or voided (0192) holds nothing; an owner's request is a fail.
    const count = (st: QaStatus) => scenarios.filter((x) => x.status === st && !(st === 'blocked' && x.out)).length;
    const requested = triage.some((c) => REQUEST_WORK.includes(c.work) && c.k === ticket.key);
    return {
      ticket,
      scenarios,
      assignees: assignments.filter((a) => a.ticket_key === ticket.key).map((a) => a.user_id),
      decided: count('pass') + count('fail'),
      // «Blocked» is neither pass nor fail (owner, 2026-10-22): it keeps the round open until it is run again —
      // unless a fail already decides it. Same rule as ticketOutcome and the tester page.
      // «Waiting for the owner» (0186) holds it open even beside a fail — until the owner looked.
      finished: stoppedAt != null || (scenarios.length > 0 && scenarios.every((x) => x.status) && !count('review')
        && (!count('blocked') || count('fail') > 0 || requested)),
      stoppedAt,
      pass: count('pass'),
      fail: count('fail'),
      blocked: count('blocked'),
      review: count('review'),
    };
  });
}

/**
 * سناریوهای پیشنهادیِ دورِ تازه برای یک تیکتِ Jira: هر سناریوی تیکت (و دورهای
 * قبلیِ وابسته به آن) که آخرین نتیجه‌اش در همه‌ی این دورها «قبول» نیست —
 * همان قاعده‌ی ticketOutcome در supabase/functions/_shared/qa-ticket.mjs — از جمله سناریویی که دورش
 * پیش از رسیدن به مرحله‌ی آن متوقف شد (0185): «قبول»ِ قدیمی‌اش حساب نیست، چون قرار بود دوباره تست شود.
 */
export function roundCandidates(jiraKey: string, rows: TicketTableRow[]): QaScenario[] {
  const mine = rows.filter((r) => r.ticket.key === jiraKey || r.ticket.jira_key === jiraKey);
  const latest = new Map<string, { s: QaScenario; at: number; status: QaStatus | null; out: boolean }>();
  for (const r of mine) for (const x of r.scenarios) {
    const last = x.attempts[x.attempts.length - 1];
    const prev = latest.get(x.scenario.id);
    if (!prev || (last && last.at > prev.at)) latest.set(x.scenario.id, { s: x.scenario, at: last?.at ?? -1, status: last?.status ?? null, out: x.out });
  }
  for (const r of mine) {
    const at = r.ticket.stopped_at ? Date.parse(r.ticket.stopped_at) : null;
    for (const x of r.scenarios) {
      const l = latest.get(x.scenario.id);
      if (at != null && x.skipped && l && l.at < at) l.status = null;
    }
  }
  // Moved to a fix ticket or voided by the owner (0192): not this ticket's to retest.
  return [...latest.values()].filter((x) => x.status !== 'pass' && !x.out).map((x) => x.s);
}

/* ===================================================================== فیلترِ و/یا */

export type QaField = 'text' | 'ticket' | 'source' | 'cat' | 'priority' | 'platform' | 'smoke'
  | 'assignee' | 'lastStatus' | 'timesTested' | 'lastDays';
export type QaOp = 'is' | 'isNot' | 'contains' | 'gte' | 'lte' | 'empty' | 'notEmpty';
export interface QaCond { field: QaField; op: QaOp; value: string }
/**
 * گروه‌ها با «یا» به هم وصل‌اند و شرط‌های داخلِ هر گروه با «و» —
 * (الف و ب) یا (ج). هر ترکیبی از و/یا را می‌شود به همین شکل نوشت.
 */
export type QaFilter = QaCond[][];

export const FIELD_OPS: Record<QaField, QaOp[]> = {
  text: ['contains'],
  ticket: ['contains', 'empty', 'notEmpty'],
  source: ['is'],
  cat: ['is', 'isNot'],
  priority: ['is', 'isNot', 'empty'],
  platform: ['is', 'isNot'],
  smoke: ['is'],
  assignee: ['is', 'isNot', 'empty', 'notEmpty'],
  lastStatus: ['is', 'isNot', 'empty'],
  timesTested: ['gte', 'lte', 'is'],
  lastDays: ['gte', 'lte', 'empty'],
};

const DAY = 24 * 60 * 60 * 1000;

/** مقدارِ یک ستون برای مقایسه؛ سکو چند مقدار دارد (qaPlatforms). */
function values(row: ScenarioTableRow, field: QaField, now: number): (string | number)[] {
  const sc = row.scenario;
  switch (field) {
    case 'text': return [`${sc.id} ${sc.title.en} ${sc.title.fa}`.toLowerCase()];
    case 'ticket': return row.tickets.map((t) => `${t.key} ${t.title ?? ''}`.toLowerCase());
    case 'source': return [sc.source ?? 'file'];
    case 'cat': return [sc.cat];
    case 'priority': return sc.priority ? [sc.priority] : [];
    case 'platform': {
      // «گوشی» با هر دو گوشی جور است و برعکس: فیلترِ «آیفون» سناریوی «گوشی» را هم نشان می‌دهد.
      const ps = qaPlatforms(sc.platform);
      if (ps.includes('phone')) ps.push('android', 'ios');
      else if (ps.includes('android') || ps.includes('ios')) ps.push('phone');
      return [...new Set(ps)];
    }
    case 'smoke': return [sc.smoke ? 'yes' : 'no'];
    case 'assignee': return row.assignees;
    case 'lastStatus': return row.lastStatus ? [row.lastStatus] : [];
    case 'timesTested': return [row.timesTested];
    case 'lastDays': return row.lastAt == null ? [] : [Math.floor((now - row.lastAt) / DAY)];
  }
}

export function matchCond(row: ScenarioTableRow, c: QaCond, now = Date.now()): boolean {
  const vs = values(row, c.field, now);
  const n = Number(c.value);
  switch (c.op) {
    case 'is': return vs.some((v) => String(v) === c.value);
    case 'isNot': return !vs.some((v) => String(v) === c.value);
    case 'contains': return vs.some((v) => String(v).includes(c.value.trim().toLowerCase()));
    case 'gte': return c.value.trim() !== '' && vs.some((v) => Number(v) >= n);
    case 'lte': return c.value.trim() !== '' && vs.some((v) => Number(v) <= n);
    case 'empty': return vs.length === 0;
    case 'notEmpty': return vs.length > 0;
  }
}

/** فیلترِ خالی (یا فقط گروه‌های خالی) یعنی همه. */
export function matchFilter(row: ScenarioTableRow, filter: QaFilter, now = Date.now()): boolean {
  const groups = filter.filter((g) => g.length);
  if (!groups.length) return true;
  return groups.some((g) => g.every((c) => matchCond(row, c, now)));
}

/* ===================================================================== گزارشِ نتیجه‌ها */

export interface TesterSummary {
  tester: QaTester;
  displayName: string;
  updatedAt: string | null;
  /** جفت‌های (تیکت، سناریو) در تیکت‌هایی که به همین تستر اساین شده. */
  assigned: number;
  done: number;
  pass: number;
  fail: number;
  blocked: number;
  inProgress: number;
  bugs: number;
  hoursMin: number;
  clockMin: number;
}

export interface ScenarioRow {
  ticket: QaTicket;
  scenario: QaScenario;
  role: QaRole;
  /** «بار»ِ چندم — همان شماره‌ای که صفحه‌ی تستر نشان می‌دهد (attempt). */
  attempt: number;
  /**
   * شناسه‌ی تستر → وضعیتِ همین بار. null = این تیکت به این تستر اساین نشده؛
   * undefined = این تستر این بار را ندارد (کمتر اجرا کرده).
   */
  status: Record<string, QaStatus | null | undefined>;
  notes: (QaNote & { user_id: string })[];
}

export interface QaReport {
  testers: TesterSummary[];
  /** هر بارِ اجرای هر (تیکت، سناریو) در تیکتی که دستِ‌کم به یک تستر اساین شده؛ اجرانشده = یک ردیفِ «بار ۱». */
  rows: ScenarioRow[];
  /** همه‌ی یادداشت‌های باگ، تازه‌ترین اول. */
  bugs: (QaNote & { user_id: string })[];
}

const live = <T extends { deleted?: boolean }>(xs: T[] | undefined) => (xs ?? []).filter((x) => !x.deleted);

export function buildQaReport(
  catalog: QaScenario[],
  tickets: QaTicket[],
  links: QaLink[],
  assignments: QaAssignment[],
  testers: QaTester[],
  states: QaStateRow[],
  events: QaEvent[] = [],
  /** صاحبانِ قبلیِ دورها (qa_round_owners_past، 0189): کسی که دوری را پس داد، ستون و کارش در همان دور را نگه می‌دارد. */
  past: QaAssignment[] = [],
): QaReport {
  const byUser = new Map(states.map((s) => [s.user_id, s]));
  const byId = new Map(catalog.map((s) => [s.id, s]));
  const has = (uid: string, key: string) => [...assignments, ...past].some((a) => a.user_id === uid && a.ticket_key === key);

  // The latest state of each (ticket, scenario, tester) — what «done / assigned» counts.
  const pairs = tickets
    .filter((t) => testers.some((x) => has(x.user_id, t.key)))
    .flatMap((ticket) => links
      .filter((l) => l.ticket_key === ticket.key)
      .sort((a, b) => ROLES.indexOf(a.role) - ROLES.indexOf(b.role) || a.sort - b.sort)
      .map((l) => ({ l, s: byId.get(l.scenario_id) }))
      .filter((x): x is { l: QaLink; s: QaScenario } => !!x.s)
      .map(({ l, s: scenario }) => ({ ticket, scenario, role: l.role })));
  const latest = (uid: string, ticket: QaTicket, scenario: QaScenario): QaStatus | null => (has(uid, ticket.key)
    ? (byUser.get(uid)?.state?.runs?.[runKey(ticket.key, scenario.id)]?.status ?? 'new') : null);

  // One row per attempt. A finished attempt is read from its `finish` event (the
  // state keeps only the current attempt); the open one from the state. A run set aside has neither.
  const finishes = events.filter(isFinish);
  const gone = discardedRuns(events.map((x) => x.e));
  const rows: ScenarioRow[] = pairs.flatMap(({ ticket, scenario, role }) => {
    const per = new Map<string, Map<number, QaStatus>>();
    const notesOf = new Map<number, ScenarioRow['notes']>();
    for (const t of testers) {
      const m = new Map<number, QaStatus>();
      for (const f of finishes.filter((x) => x.user_id === t.user_id && x.e.sid === scenario.id && x.e.k === ticket.key)
        .sort((a, b) => a.e.t - b.e.t)) {
        if (f.e.a != null && f.e.d?.status) m.set(f.e.a, f.e.d.status);
      }
      const st = byUser.get(t.user_id)?.state;
      const run = st?.runs?.[runKey(ticket.key, scenario.id)];
      if (run && run.attempt && !m.has(run.attempt)) m.set(run.attempt, run.status);
      for (const n of live(st?.notes)) {
        if (n.sid !== scenario.id || n.k !== ticket.key) continue;
        const a = n.a ?? 1;
        if (gone.has(discardKey(n.k, n.sid, a))) continue;
        if (!m.has(a)) m.set(a, 'progress');
        notesOf.set(a, [...(notesOf.get(a) ?? []), { ...n, user_id: t.user_id }]);
      }
      per.set(t.user_id, m);
    }
    const attempts = [...new Set([...per.values()].flatMap((m) => [...m.keys()]))].sort((a, b) => a - b);
    return (attempts.length ? attempts : [1]).map((attempt) => {
      const status: ScenarioRow['status'] = {};
      for (const t of testers) {
        status[t.user_id] = !has(t.user_id, ticket.key) ? null
          // never run by this tester: «not started» on the first row, nothing on the rest
          : per.get(t.user_id)!.get(attempt) ?? (per.get(t.user_id)!.size === 0 && attempt === (attempts[0] ?? 1) ? 'new' : undefined);
      }
      const notes = (notesOf.get(attempt) ?? []).sort((a, b) => b.t - a.t);
      return { ticket, scenario, role, attempt, status, notes };
    });
  });

  const summaries = testers.map((tester): TesterSummary => {
    const row = byUser.get(tester.user_id);
    const st = row?.state ?? {};
    const sts = pairs.map((p) => latest(tester.user_id, p.ticket, p.scenario)).filter((x): x is QaStatus => x != null);
    const count = (s: QaStatus) => sts.filter((x) => x === s).length;
    return {
      tester,
      // نامی که ادمین داده بر نامی که تستر خودش در صفحه نوشته مقدم است.
      displayName: tester.name || st.tester || tester.email,
      updatedAt: row?.updated_at ?? null,
      assigned: sts.length,
      done: sts.filter((x) => FINISHED.includes(x)).length,
      pass: count('pass'),
      fail: count('fail'),
      blocked: count('blocked'),
      inProgress: count('progress'),
      bugs: live(st.notes).filter((n) => n.bug).length,
      hoursMin: live(st.hours).reduce((a, h) => a + (h.min || 0), 0),
      clockMin: clockMinutes(events.filter((e) => e.user_id === tester.user_id).map((e) => e.e)),
    };
  });

  const bugs = testers
    .flatMap((t) => live(byUser.get(t.user_id)?.state.notes).filter((n) => n.bug).map((n) => ({ ...n, user_id: t.user_id })))
    .sort((a, b) => b.t - a.t);

  return { testers: summaries, rows, bugs };
}

export interface QaInvite { email: string; name: string | null; is_default?: boolean; invited_at: string }

/**
 * پیش‌فرضِ «اساین به…»: تستر یا دعوت‌شده‌ی علامت‌خورده (کلیدِ دعوت‌شده
 * `invite:<email>`)، وگرنه اگر فقط یک گزینه هست همان، وگرنه هیچ.
 */
export function defaultTarget(testers: QaTester[], invites: QaInvite[] = []): string | null {
  const keys = [
    ...testers.filter((t) => t.active).map((t) => ({ key: t.user_id, on: !!t.is_default })),
    ...invites.map((i) => ({ key: inviteKey(i.email), on: !!i.is_default })),
  ];
  return keys.find((k) => k.on)?.key ?? (keys.length === 1 ? keys[0].key : null);
}

/**
 * همه‌ی یادداشت‌های زنده‌ی یک سناریو از همه‌ی تسترها، دسته‌شده بر اساسِ صفحه —
 * برای پنجره‌ی «مرورِ سناریو» که هر یادداشت را زیرِ همان قدمی نشان می‌دهد که
 * رویش نوشته شده. شماره‌ی صفحه همان pِ صفحه‌ی تستر است: 0 = نمای کلی،
 * 1..n = قدم‌ها، n+1 = نتیجه. جدیدترین اول.
 */
export function scenarioNotes(states: QaStateRow[], sid: string): Map<number, (QaNote & { user_id: string })[]> {
  const out = new Map<number, (QaNote & { user_id: string })[]>();
  states
    .flatMap((st) => live(st.state.notes).filter((n) => n.sid === sid).map((n) => ({ ...n, user_id: st.user_id })))
    .sort((a, b) => b.t - a.t)
    .forEach((n) => { const p = n.p ?? 0; out.set(p, [...(out.get(p) ?? []), n]); });
  return out;
}

/** تیکتِ Jiraی یک باگ (qa_bug_jira، 0167) و وضعیتش که qa-jira از Jira می‌خواند (0168). */
export interface QaBugJira {
  user_id: string; note_id: string; issue_key: string | null; error: string | null;
  jira_status?: string | null; jira_done?: boolean; status_checked_at?: string | null;
}
/** باگی که ادمین «رسیدگی شد» علامت زده (qa_bug_handled، 0168). */
export interface QaBugHandled { user_id: string; note_id: string; handled_at: string }

/* ===================================================================== نظرِ خودِ تستر (0186)
   یادداشتِ باگ (تا 0192 پیشنهاد هم) که تسترِ غیرادمین گفت «نظرِ خودم هم هست» یا «مطمئن نیستم»: پیش از اثر
   مدیر نگاهش می‌کند — «تأیید» (همان اثرِ همیشگی) یا «فقط یادداشت». همان قاعده‌ی isInterp در
   supabase/functions/_shared/qa-ticket.mjs، qa/index.html و private.qa_interp_sev (مایگریشن 0186). */
export const INTERP_SEVS = ['critical', 'major', 'minor', 'cosmetic'];   // suggest left in WHOSUP-382 (0192): it gets a case
export const isInterp = (n: Pick<QaNote, 'sev' | 'interp'>) => INTERP_SEVS.includes(n.sev ?? '') && ['yes', 'unsure'].includes(n.interp ?? '');
export type QaDecision = 'approved' | 'dismissed';
export interface QaNoteReview { user_id: string; note_id: string; decision: QaDecision; decided_at: string }
export interface InterpNote { note: QaNote & { user_id: string }; decision: QaDecision | null; decidedAt: string | null }

/** همه‌ی یادداشت‌های «نظرِ تستر»، منتظرها اول و بعد تازه‌ترین. */
export function interpNotes(states: QaStateRow[], reviews: QaNoteReview[]): InterpNote[] {
  const by = new Map(reviews.map((r) => [`${r.user_id}/${r.note_id}`, r]));
  return states
    .flatMap((s) => live(s.state?.notes).filter(isInterp).map((n) => ({ ...n, user_id: s.user_id })))
    .map((note) => {
      const r = by.get(`${note.user_id}/${note.id}`);
      return { note, decision: r?.decision ?? null, decidedAt: r?.decided_at ?? null };
    })
    .sort((a, b) => Number(!!a.decision) - Number(!!b.decision) || b.note.t - a.note.t);
}

/**
 * وضعیتِ یک باگ در سربرگِ «تستِ دستی»:
 *   'handled' — ادمین علامت زده رسیدگی شد (مقدم بر Jira: تصمیمِ صریحِ ادمین)
 *   'done'    — تیکتش در Jira در دسته‌ی Done است، پس اعمال‌شده حساب می‌شود
 *   'open'    — بقیه، از جمله باگی که هنوز تیکت ندارد یا ساختنش خطا داده
 * فیلترِ پیش‌فرض فقط 'open' را نشان می‌دهد.
 */
export type QaBugState = 'open' | 'handled' | 'done';
export function bugState(jira: QaBugJira | undefined, handled: QaBugHandled | undefined): QaBugState {
  if (handled) return 'handled';
  if (jira?.issue_key && jira.jira_done) return 'done';
  return 'open';
}
export const bugKey = (userId: string, noteId: string) => `${userId}/${noteId}`;
